import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  Search as SearchIcon,
  ArrowRight,
  AlertCircle,
  Loader2,
  HardHat,
  ShieldCheck,
  Phone,
  GraduationCap,
  Palmtree,
  Pencil,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { bikes } from "../mock/mock";
import {
  computeDurationDetail,
  buildLocalDate,
  getRentalHours,
} from "../lib/duration";
import { useBookingStatus } from "../lib/bookingStatus";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shop hours per pickup point
const SHOP_HOURS = {
  Udupi: { startMin: 7 * 60 + 30, endMin: 19 * 60 + 30 },
  Manipal: { startMin: 9 * 60, endMin: 19 * 60 + 30 },
};

const minToValue = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(
    2,
    "0"
  )}`;

const minToLabel = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const hour12 = ((h + 11) % 12) + 1;
  return `${String(hour12).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${
    h < 12 ? "AM" : "PM"
  }`;
};

const valueToMin = (v) => {
  if (!v) return null;
  const [h, m] = v.split(":").map(Number);
  return h * 60 + m;
};

const slotsFor = (pickup) => {
  const { startMin, endMin } = SHOP_HOURS[pickup] || SHOP_HOURS.Udupi;
  const slots = [];
  for (let m = startMin; m <= endMin; m += 30) {
    slots.push({ value: minToValue(m), label: minToLabel(m), min: m });
  }
  return slots;
};

// IST (Asia/Kolkata) today ISO + current minutes-from-midnight
const getIST = () => {
  const d = new Date();
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value])
  );
  return {
    todayISO: `${parts.year}-${parts.month}-${parts.day}`,
    nowMins: parseInt(parts.hour, 10) * 60 + parseInt(parts.minute, 10),
  };
};

const roundUpTo30 = (m) => Math.ceil(m / 30) * 30;

// "YYYY-MM-DD" + "HH:MM" → "07-Oct-2026 04:00 PM" (Zoho API format)
const formatZohoDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const monthShort = new Date(y, m - 1, d).toLocaleString("en-US", {
    month: "short",
  });
  const hour12 = ((hh + 11) % 12) + 1;
  const ampm = hh < 12 ? "AM" : "PM";
  return `${String(d).padStart(2, "0")}-${monthShort}-${y} ${String(
    hour12
  ).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${ampm}`;
};

// "YYYY-MM-DD" + "HH:MM" → "07 Oct 2026 • 04:00 PM" (display format)
const formatDisplayDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const monthShort = new Date(y, m - 1, d).toLocaleString("en-US", {
    month: "short",
  });
  const hour12 = ((hh + 11) % 12) + 1;
  const ampm = hh < 12 ? "AM" : "PM";
  return `${String(d).padStart(2, "0")} ${monthShort} ${y} • ${String(
    hour12
  ).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${ampm}`;
};

const findImageForModel = (name) => {
  const n = (name || "").toLowerCase();
  const match = bikes.find((b) => {
    const bn = b.name.toLowerCase();
    const nn = n.replace(/[^a-z0-9]/g, "");
    const bb = bn.replace(/[^a-z0-9]/g, "");
    return nn.includes(bb) || bb.includes(nn.split("bs")[0]);
  });
  return match ? match.image : null;
};

const Search = () => {
  const navigate = useNavigate();
  const routeLoc = useLocation();
  const { online: bookingOnline } = useBookingStatus();

  const qs = useMemo(
    () => new URLSearchParams(routeLoc.search),
    [routeLoc.search]
  );

  const [pickup, setPickup] = useState(qs.get("pickup") || "Udupi");
  const [startDate, setStartDate] = useState(qs.get("startDate") || "");
  const [startTime, setStartTime] = useState(qs.get("startTime") || "");
  const [endDate, setEndDate] = useState(qs.get("endDate") || "");
  const [endTime, setEndTime] = useState(qs.get("endTime") || "");

  // The exact search that produced the current result (snapshot). Displayed in
  // the summary bar so it reflects what the customer actually submitted —
  // immune to any AM/PM echo-back issues from the backend.
  const [committed, setCommitted] = useState(() =>
    qs.get("pickup") && qs.get("startDate") && qs.get("endDate")
      ? {
          pickup: qs.get("pickup"),
          startDate: qs.get("startDate"),
          startTime: qs.get("startTime"),
          endDate: qs.get("endDate"),
          endTime: qs.get("endTime"),
        }
      : null
  );

  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const { todayISO, nowMins } = useMemo(() => getIST(), [startDate, pickup]);

  const timeSlots = useMemo(() => slotsFor(pickup), [pickup]);

  // On pickup change clear invalid times (consistent with homepage widget)
  useEffect(() => {
    const { startMin, endMin } = SHOP_HOURS[pickup];
    if (startTime) {
      const m = valueToMin(startTime);
      if (m < startMin || m > endMin) setStartTime("");
    }
    if (endTime) {
      const m = valueToMin(endTime);
      if (m < startMin || m > endMin) setEndTime("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup]);

  // Start-time options honouring today's past-time filter in IST
  const startSlots = useMemo(() => {
    if (!startDate || startDate !== todayISO) return timeSlots;
    const minAllowed = Math.max(
      SHOP_HOURS[pickup].startMin,
      roundUpTo30(nowMins)
    );
    return timeSlots.filter((s) => s.min >= minAllowed);
  }, [timeSlots, startDate, todayISO, pickup, nowMins]);

  // End-time options strictly after start time when same day
  const endSlots = useMemo(() => {
    let slots = timeSlots;
    if (endDate && endDate === todayISO) {
      const minAllowed = Math.max(
        SHOP_HOURS[pickup].startMin,
        roundUpTo30(nowMins)
      );
      slots = slots.filter((s) => s.min >= minAllowed);
    }
    if (startDate && endDate && startDate === endDate && startTime) {
      const sM = valueToMin(startTime);
      slots = slots.filter((s) => s.min > sM);
    }
    return slots;
  }, [timeSlots, endDate, todayISO, pickup, nowMins, startDate, startTime]);

  const canonical = "https://ezrental.in/search";

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    setError(null);

    if (!pickup || !startDate || !startTime || !endDate || !endTime) {
      setError("Please select your start and end date and time.");
      return;
    }

    const { todayISO: istToday, nowMins: istNow } = getIST();
    if (startDate < istToday) {
      setError("Please select a current or future date and time.");
      return;
    }
    if (startDate === istToday && valueToMin(startTime) < roundUpTo30(istNow)) {
      setError("Please select a current or future date and time.");
      return;
    }
    if (endDate < startDate) {
      setError("Drop-off date cannot be before pickup date.");
      return;
    }
    if (startDate === endDate && valueToMin(endTime) <= valueToMin(startTime)) {
      setError("Drop-off time must be after pickup time.");
      return;
    }

    // Online bookings require a minimum of 24 hours (one full day).
    const totalHours = getRentalHours(startDate, startTime, endDate, endTime);
    if (totalHours < 24) {
      setError(
        "Minimum rental is 1 full day (24 hours). Please select an end date and time at least 24 hours after your start date and time."
      );
      return;
    }

    const { startMin, endMin } = SHOP_HOURS[pickup];
    const sM = valueToMin(startTime);
    const eM = valueToMin(endTime);
    if (sM < startMin || sM > endMin || eM < startMin || eM > endMin) {
      setError(
        `For ${pickup}, pickup and drop-off times must be between ${minToLabel(
          startMin
        )} and ${minToLabel(endMin)}.`
      );
      return;
    }

    // Zoho already combines day-rate + extra-hour-rate internally when given
    // the customer's actual start → end range (we verified: for "1 Day 6
    // Hours" it returns a single combined price). We send the exact selected
    // range and use Zoho's returned price verbatim — no local recomputation,
    // no dual probe, no hourly fallback. This is also what the business rule
    // requires: "The website must display the price returned by Zoho; it
    // must not calculate a separate price or maintain its own pricing master."
    const startStr = formatZohoDate(startDate, startTime);
    const endStr = formatZohoDate(endDate, endTime);

    setLoading(true);
    setResult(null);
    try {
      const resp = await axios.post(`${API}/search-availability`, {
        pickup_point: pickup,
        start_datetime: startStr,
        end_datetime: endStr,
      });
      setResult(resp.data);
      setCommitted({ pickup, startDate, startTime, endDate, endTime });
      setShowForm(false);
      const params = new URLSearchParams({
        pickup,
        startDate,
        startTime,
        endDate,
        endTime,
      });
      navigate(
        { pathname: "/search", search: `?${params.toString()}` },
        { replace: true }
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not reach the pricing service. Please try again shortly.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-run when landing with full query string
  useEffect(() => {
    if (
      qs.get("pickup") &&
      qs.get("startDate") &&
      qs.get("startTime") &&
      qs.get("endDate") &&
      qs.get("endTime")
    ) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = committed
    ? {
        pickup: committed.pickup,
        start: formatDisplayDateTime(committed.startDate, committed.startTime),
        end: formatDisplayDateTime(committed.endDate, committed.endTime),
      }
    : null;

  // Local effective duration using the shared 6-hour rule — single source
  // of truth that overrides anything the API echoes back.
  const effectiveDuration = useMemo(() => {
    if (!committed) return null;
    const sDt = buildLocalDate(committed.startDate, committed.startTime);
    const eDt = buildLocalDate(committed.endDate, committed.endTime);
    return computeDurationDetail(sDt, eDt);
  }, [committed]);

  // True whenever the currently selected rental is non-zero but < 24 real
  // hours. Driven by `committed` (same values the summary bar shows) so the
  // warning is visible immediately without needing to open Modify Search.
  const committedBelow24h = useMemo(() => {
    if (!committed) return false;
    const hrs = getRentalHours(
      committed.startDate,
      committed.startTime,
      committed.endDate,
      committed.endTime
    );
    return hrs > 0 && hrs < 24;
  }, [committed]);

  const minStartDate = todayISO;
  const minEndDate = startDate || todayISO;

  return (
    <div className="bg-neutral-50 text-neutral-900 antialiased min-h-screen flex flex-col">
      <SEO
        title="Search Scooter Availability | RR Bros Bike Rental"
        description="Check live scooter availability and prices for bike & scooty rental in Udupi and Manipal."
        canonical={canonical}
        keywords="scooter availability Udupi, bike rental price Udupi, scooty rental Manipal price, RR Bros booking"
      />
      <Header />

      {/* Compact search summary bar */}
      <section
        className="bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-800 text-white py-5 sm:py-7"
        data-testid="search-summary-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {summary ? (
            <div
              className="bg-white text-neutral-900 rounded-2xl shadow-xl border border-white/10 p-3 sm:p-4"
              data-testid="search-summary-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:border-r sm:border-neutral-100 sm:pr-4">
                  <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-sm font-black text-neutral-900 leading-tight"
                      data-testid="summary-pickup"
                    >
                      {summary.pickup}
                    </p>
                    {effectiveDuration && (
                      <p className="text-[11px] text-neutral-500 leading-tight">
                        <span className="font-semibold text-neutral-700">
                          {effectiveDuration.display}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 leading-tight">
                      From
                    </p>
                    <p
                      className="text-xs sm:text-sm font-bold text-neutral-900 truncate"
                      data-testid="summary-start"
                    >
                      {summary.start}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 leading-tight">
                      Until
                    </p>
                    <p
                      className="text-xs sm:text-sm font-bold text-neutral-900 truncate"
                      data-testid="summary-end"
                    >
                      {summary.end}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowForm((v) => !v)}
                  data-testid="modify-search-btn"
                  className="inline-flex items-center justify-center gap-1.5 h-10 sm:h-11 px-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-wider shadow-md transition-colors whitespace-nowrap"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {showForm ? "Hide" : "Modify Search"}
                  {showForm ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {committedBelow24h && (
                <div
                  role="alert"
                  data-testid="min-rental-warning"
                  className="mt-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2.5"
                >
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-xs sm:text-sm leading-snug">
                    <p className="font-black">
                      Minimum rental is 1 full day (24 hours).
                    </p>
                    <p className="text-[11px] sm:text-xs mt-0.5">
                      Please select an end date and time at least 24 hours
                      after your start time.{" "}
                      <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="font-black underline underline-offset-2 hover:text-amber-700"
                      >
                        Change dates
                      </button>
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-black">
                Search your <span className="text-orange-500">next ride</span>
              </h1>
              <p className="mt-2 text-sm text-neutral-300">
                Enter your dates below to see live availability.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Collapsible modify-search form */}
      {(showForm || !committed) && (
        <section className="relative -mt-2 z-10" data-testid="modify-form-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <form
              onSubmit={runSearch}
              noValidate
              className="bg-white rounded-2xl shadow-xl border border-neutral-100 p-4 sm:p-5 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 sm:gap-4">
                {/* Pickup */}
                <div className="md:col-span-1">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">
                    Pickup Point
                  </label>
                  <Select value={pickup} onValueChange={setPickup}>
                    <SelectTrigger
                      className="h-12 bg-neutral-50 rounded-xl"
                      data-testid="modify-pickup-trigger"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Udupi">Udupi</SelectItem>
                      <SelectItem value="Manipal">Manipal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Start Date */}
                <div className="md:col-span-1">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">
                    Start Date
                  </label>
                  <label
                    htmlFor="modify-start-date"
                    className="relative flex items-center h-12 bg-neutral-50 border border-neutral-200 rounded-xl px-3 cursor-pointer"
                  >
                    <CalendarIcon className="w-4 h-4 text-orange-500 flex-shrink-0 mr-2" />
                    <input
                      id="modify-start-date"
                      type="date"
                      min={minStartDate}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-transparent text-sm font-semibold text-neutral-900 outline-none appearance-none"
                      data-testid="modify-start-date"
                    />
                  </label>
                </div>

                {/* Start Time */}
                <div className="md:col-span-1">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">
                    Start Time
                  </label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger
                      className="h-12 bg-neutral-50 rounded-xl data-[placeholder]:text-neutral-400"
                      data-testid="modify-start-time-trigger"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <SelectValue placeholder="Time" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {startSlots.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-neutral-500">
                          No slots left today.
                        </div>
                      ) : (
                        startSlots.map((t) => (
                          <SelectItem key={`ms-${t.value}`} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* End Date */}
                <div className="md:col-span-1">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">
                    End Date
                  </label>
                  <label
                    htmlFor="modify-end-date"
                    className="relative flex items-center h-12 bg-neutral-50 border border-neutral-200 rounded-xl px-3 cursor-pointer"
                  >
                    <CalendarIcon className="w-4 h-4 text-orange-500 flex-shrink-0 mr-2" />
                    <input
                      id="modify-end-date"
                      type="date"
                      min={minEndDate}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-transparent text-sm font-semibold text-neutral-900 outline-none appearance-none"
                      data-testid="modify-end-date"
                    />
                  </label>
                </div>

                {/* End Time */}
                <div className="md:col-span-1">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">
                    End Time
                  </label>
                  <Select value={endTime} onValueChange={setEndTime}>
                    <SelectTrigger
                      className="h-12 bg-neutral-50 rounded-xl data-[placeholder]:text-neutral-400"
                      data-testid="modify-end-time-trigger"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        <SelectValue placeholder="Time" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {endSlots.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-neutral-500">
                          No valid slots.
                        </div>
                      ) : (
                        endSlots.map((t) => (
                          <SelectItem key={`me-${t.value}`} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {error && (
                <div
                  role="alert"
                  className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 flex items-start gap-2 text-sm"
                  data-testid="modify-search-error"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="modify-search-submit"
                className="w-full h-12 rounded-xl bg-yellow-400 hover:bg-yellow-500 disabled:bg-neutral-200 disabled:text-neutral-500 text-neutral-900 font-black text-sm uppercase tracking-wider inline-flex items-center justify-center gap-2 shadow-md transition-all"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <SearchIcon className="w-4 h-4" />
                )}
                Search
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Results */}
      <section className="flex-1 py-6 sm:py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {bookingOnline === false && (
            <div
              data-testid="bookings-off-banner"
              role="status"
              className="mb-5 sm:mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 sm:py-4 flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-black text-red-800">
                  Online bookings are temporarily unavailable.
                </p>
                <p className="text-xs text-red-700 mt-0.5">
                  Please call us at{" "}
                  <a
                    href="tel:+917067706719"
                    className="font-black underline"
                    data-testid="bookings-off-call-link"
                  >
                    070677 06719
                  </a>{" "}
                  to book your scooter.
                </p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-3" />
              <p className="text-sm">Checking live availability…</p>
            </div>
          )}

          {!loading && result && result.success && result.vehicles?.length === 0 && (
            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center">
              <h2 className="text-xl font-black text-neutral-900">
                No scooters available
              </h2>
              <p className="text-neutral-600 mt-2 text-sm">
                We don&apos;t have scooters available for the selected dates.
                Try different dates.
              </p>
            </div>
          )}

          {!loading && result && result.success && result.vehicles?.length > 0 && (
            <>
              <div className="mb-5 sm:mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                  {(() => {
                    const allMonthly = result.vehicles.every(
                      (v) => v.monthly_booking === true
                    );
                    const pickupName = committed?.pickup || result.pickup_point;
                    if (allMonthly) {
                      return (
                        <h2
                          className="text-lg sm:text-xl font-black text-neutral-900"
                          data-testid="results-heading"
                        >
                          Monthly rentals in{" "}
                          <span className="text-orange-500">{pickupName}</span>
                        </h2>
                      );
                    }
                    const dailyVehicles = result.vehicles.filter(
                      (v) => v.monthly_booking !== true
                    );
                    const inStockModels = dailyVehicles.filter(
                      (v) => Number(v.available_quantity) > 0
                    ).length;
                    return (
                      <h2
                        className="text-lg sm:text-xl font-black text-neutral-900"
                        data-testid="results-heading"
                      >
                        {inStockModels > 0
                          ? `${inStockModels} scooter model${
                              inStockModels === 1 ? "" : "s"
                            } available in `
                          : "Available scooters in "}
                        <span className="text-orange-500">{pickupName}</span>
                      </h2>
                    );
                  })()}
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Live availability &amp; pricing
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {result.vehicles.map((v, idx) => {
                  const img = findImageForModel(v.model);
                  const isMonthly = v.monthly_booking === true;
                  const bookingOffline = bookingOnline === false;
                  const adminBlocked = v.blocked_by_admin === true;
                  const outOfStock =
                    (v.available_quantity || 0) <= 0 || bookingOffline;

                  const goBook = () => {
                    if (!committed) return;
                    const params = new URLSearchParams({
                      pickup: committed.pickup,
                      startDate: committed.startDate,
                      startTime: committed.startTime,
                      endDate: committed.endDate,
                      endTime: committed.endTime,
                    });
                    navigate("/book", {
                      state: {
                        pickup_point: committed.pickup,
                        start_datetime: formatZohoDate(
                          committed.startDate,
                          committed.startTime
                        ),
                        end_datetime: formatZohoDate(
                          committed.endDate,
                          committed.endTime
                        ),
                        duration: result.duration,
                        model: v.model,
                        unit_price: v.price,
                        available_quantity: v.available_quantity,
                        search_qs: `?${params.toString()}`,
                      },
                    });
                  };

                  return (
                    <article
                      key={`${v.model}-${idx}`}
                      data-testid={`result-card-${idx}`}
                      className="group bg-white rounded-2xl border border-neutral-100 overflow-hidden hover:shadow-xl transition-all flex flex-col"
                    >
                      <div className="relative h-48 bg-gradient-to-br from-neutral-100 to-neutral-50 flex items-center justify-center p-4">
                        {img ? (
                          <img
                            src={img}
                            alt={`Rent ${v.model} at RR Bros ${
                              committed?.pickup || result.pickup_point
                            }`}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="text-neutral-300 text-6xl font-black">
                            RR
                          </div>
                        )}
                        <div className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow">
                          {isMonthly ? "Monthly" : "Scooter"}
                        </div>
                        {!isMonthly && outOfStock && (
                          <div
                            data-testid={`unavailable-badge-${idx}`}
                            className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          >
                            {adminBlocked
                              ? "Unavailable for selected dates"
                              : "Currently Unavailable"}
                          </div>
                        )}
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-black text-lg text-neutral-900 leading-tight">
                          {v.model}
                        </h3>
                        {/* pricing_category hidden for daily rentals; still shown on monthly as it reads
                            as a plain "Monthly" label if present. */}
                        {isMonthly && v.pricing_category && (
                          <p className="text-xs text-neutral-500 mt-0.5">
                            {v.pricing_category}
                          </p>
                        )}

                        {!isMonthly && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-xs text-neutral-600">
                            <div className="flex items-center gap-1.5">
                              <HardHat className="w-3.5 h-3.5 text-orange-500" />
                              <span>1 helmet free</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5 text-orange-500" />
                              <span>24 hrs basis</span>
                            </div>
                          </div>
                        )}

                        {isMonthly ? (
                          <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-col gap-3">
                            <div className="space-y-2">
                              {v.has_student_monthly_price &&
                                v.monthly_student_price != null && (
                                  <div
                                    className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-3 py-2"
                                    data-testid={`monthly-student-price-${idx}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <GraduationCap className="w-4 h-4 text-orange-600" />
                                      <span className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                                        Student Monthly
                                      </span>
                                    </div>
                                    <span className="text-lg font-black text-neutral-900">
                                      ₹
                                      {Number(
                                        v.monthly_student_price
                                      ).toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                )}
                              {v.has_tourist_monthly_price &&
                                v.monthly_tourist_price != null && (
                                  <div
                                    className="flex items-center justify-between bg-sky-50 border border-sky-100 rounded-xl px-3 py-2"
                                    data-testid={`monthly-tourist-price-${idx}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Palmtree className="w-4 h-4 text-sky-600" />
                                      <span className="text-xs font-bold text-neutral-700 uppercase tracking-wider">
                                        Tourist Monthly
                                      </span>
                                    </div>
                                    <span className="text-lg font-black text-neutral-900">
                                      ₹
                                      {Number(
                                        v.monthly_tourist_price
                                      ).toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                )}
                              {!v.has_student_monthly_price &&
                                !v.has_tourist_monthly_price && (
                                  <p className="text-xs text-neutral-500">
                                    Monthly pricing available on request.
                                  </p>
                                )}
                            </div>

                            <div
                              className="bg-neutral-900 text-white rounded-xl p-3 text-center"
                              data-testid={`monthly-cta-${idx}`}
                            >
                              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-400">
                                Monthly Booking
                              </p>
                              <p className="text-xs text-neutral-300 mt-1">
                                For monthly rental booking, please call us
                              </p>
                              <a
                                href="tel:+917067706719"
                                data-testid={`monthly-phone-${idx}`}
                                className="mt-2 inline-flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-neutral-900 font-black text-sm tracking-wide transition-colors"
                              >
                                <Phone className="w-4 h-4" />
                                070677 06719
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-4 pt-4 border-t border-neutral-100 flex items-end justify-between">
                            <div>
                              <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">
                                Total{" "}
                                {effectiveDuration
                                  ? `· ${effectiveDuration.display}`
                                  : v.duration
                                  ? `· ${v.duration}`
                                  : ""}
                              </p>
                              <p className="text-2xl font-black text-neutral-900">
                                ₹{Number(v.price || 0).toLocaleString("en-IN")}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => !outOfStock && goBook()}
                              disabled={outOfStock}
                              data-testid={`book-now-${idx}`}
                              className={`inline-flex items-center gap-1.5 px-5 h-11 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                                outOfStock
                                  ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                                  : "bg-orange-500 hover:bg-orange-600 text-white shadow-md"
                              }`}
                            >
                              {outOfStock ? "Unavailable" : "Book Now"}
                              {!outOfStock && <ArrowRight className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        )}
                        {!isMonthly && bookingOffline && (
                          <p
                            data-testid={`bookings-off-note-${idx}`}
                            className="mt-3 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2 leading-snug"
                          >
                            Online bookings are temporarily unavailable. Please
                            call us at{" "}
                            <a
                              href="tel:+917067706719"
                              className="font-black underline"
                            >
                              070677 06719
                            </a>
                            .
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>

              <p className="text-[11px] text-neutral-500 mt-6 text-center">
                * Prices and availability are fetched live from our rental
                system and are subject to change.
              </p>
            </>
          )}

          {!loading && result && !result.success && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm">
                  {result.message ||
                    "We couldn't find availability for the chosen dates. Please try different dates."}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Search;
